import { native } from '@forest/native-services'
import { cn } from '@forest/shared'
import { dialog } from '@forest/shared/solid'
import {
  createEffect,
  createMemo,
  createSignal,
  For,
  on,
  onCleanup,
  Show,
  untrack,
  type Accessor,
} from 'solid-js'
import { logButtonClick, logComponentShow, logPageView } from '~/lib/analytics'
import {
  CLASSIC_CONTENT_FILTERS,
  DEFAULT_CLASSIC_CONTENT_FILTER,
  offersClassicContentFilter,
  resolveClassicContentFilter,
  type ClassicContentFilter,
} from '~/lib/classic-content-filter'
import type { CatalogueCurrency } from '~/lib/catalogue-price'
import type { ClassicSort } from '~/lib/classic-sorting'
import { logNonFatal } from '~/lib/diagnostics'
import {
  resolveDeepLinkProduct,
  resolveLandingTab,
  type LaunchProduct,
  type ShopTab,
} from '~/lib/deep-link'
import { prefersReducedMotion } from '~/lib/motion'
import { preloadImage } from '~/lib/preload-image'
import { whenAtRest } from '~/lib/scroll-rest'
import { tabParam } from '~/lib/params'
import { createStarterPackOfferMemory } from '~/lib/starter-pack-offer-memory'
import { CELEBRATION_DURATION_MS } from '~/lib/unlock-celebration'
import { m } from '~/paraglide/messages'
import type { PassivePlusState } from '~/services/bootstrap'
import type { CatalogueContent, CatalogueProduct } from '~/services/catalogue'
import {
  activeFirstPurchaseBonus,
  anyFirstPurchaseBonusActive,
  type CoinProduct,
  type CurrencyCatalogue as CurrencyCatalogueData,
  type CurrencyKind,
  type CurrencyProduct,
  type FirstPurchaseBonusOf,
  type TimeCrystalProduct,
} from '~/services/currency-catalogue'
import {
  FIRST_REVEAL_COUNT,
  hasVisibleSlots,
  type CatalogueGridSlot,
} from '~/store/catalogue-grid'
import type { DailyPicksRefreshState } from '~/store/daily-picks-refresh'
import type {
  DailyPicksRefreshTap,
  RefreshShortage,
} from '~/store/daily-picks-refresh-flow'
import type { HeroSlot } from '~/store/hero-row'
import type { StarterPack } from '~/services/starter-pack'
import type { StarterPackSlot } from '~/store/starter-pack'
import type { ShopBalances } from '~/store/session-ledger'
import { BalancePills } from './balance-pills'
import {
  ClassicControlsPlaceholder,
  REVEAL_HIDDEN_CLASS,
  REVEAL_TRANSITION_CLASS,
} from './boot-skeleton'
import { CatalogueGridPlaceholder } from './catalogue-card'
import { ClassicCatalogue } from './classic-catalogue'
import { SkeletonCrossfade } from './skeleton-crossfade'
import type { CurrencyArtworkStatus } from './currency-artwork'
import {
  CurrencyCatalogue,
  type CurrencySkeletonCounts,
} from './currency-catalogue'
import { CurrencyExchangeFlow } from './currency-exchange-flow'
import { FeaturedCatalogue } from './featured-catalogue'
import { SortIcon } from './icons/sort'
import { openLandSkinApplyPrompt } from './land-skin-apply-prompt'
import {
  CATALOGUE_COLUMN_CLASS,
  CATALOGUE_TOP,
  CATALOGUE_TOP_UNDER_CLASSIC_CONTROLS,
  CLASSIC_CHIP_SIZE_CLASS,
  CLASSIC_CONTROLS_ROW_CLASS,
  CLASSIC_SORT_SIZE_CLASS,
  CloseShopButton,
  HEADER_ROW_CLASS,
  ShopHeader,
  TAB_ROW_CLASS,
  TAB_SIZE_CLASS,
} from './shop-header'
import { LandSkinDetail } from './land-skin-detail'
import { StarterPackDetail } from './starter-pack-detail'
import { StarterPackOfferDialog } from './starter-pack-offer-dialog'
import { StarterPackPurchaseFlow } from './starter-pack-purchase-flow'
import { TimeCrystalPurchaseFlow } from './time-crystal-purchase-flow'
import { TreeDetail } from './tree-detail'
import { TreeUnlockFlow } from './tree-unlock-flow'

// Each tab button: the boot skeleton's tab bars share its size class.
const TAB_BUTTON_CLASS = cn(
  'relative text-base font-bold text-ink transition-opacity aria-selected:opacity-100 aria-[selected=false]:opacity-50',
  TAB_SIZE_CLASS,
)

// The dialog's analytics identity carries the Store product id — never the
// content gid (GLOSSARY.md "Shop analytics names").
const productDialogName = (product: CatalogueProduct) =>
  `shop_product_dialog_${product.id}` as const

const pageNameByTab = {
  classic: 'shop_classic',
  currency: 'shop_top_up',
  featured: 'shop_featured',
} as const satisfies Record<ShopTab, Parameters<typeof logPageView>[0]>

const nextSort: Record<ClassicSort, ClassicSort> = {
  default: 'price-descending',
  'price-descending': 'price-ascending',
  'price-ascending': 'default',
}

// The chips' copy: the existing `tree` / `land` keys. Spelled out per kind so
// every `m.*` access stays static for tree shaking.
const contentFilterLabel: Record<ClassicContentFilter, () => string> = {
  land_skin: m.land,
  tree_type: m.tree,
}

interface ProductDetailState {
  fromDailyPick: boolean
  isOpen: boolean
  product?: CatalogueProduct
  // Whether the launch deep link opened this detail rather than a card tap;
  // the tree detail swaps its blurred overlay for a solid cover then.
  fromDeepLink: boolean
}

// Catalogue identity is (content type, gid): tree and land-skin gids are
// separate namespaces that must never mark each other (GLOSSARY.md "Owned
// land skin"). Session-local ownership and celebrations key on this.
type CatalogueKey = `${CatalogueContent}:${number}`
const catalogueKey = (content: CatalogueContent, gid: number): CatalogueKey =>
  `${content}:${gid}`

export function ShopShell(props: {
  balances: Accessor<ShopBalances | undefined>
  // Whether the Classic tab's catalogue shows its cards rather than its
  // skeleton (GLOSSARY.md "Classic reveal"); always true on a Classic
  // landing, where the Shop reveal covers it.
  classicRevealed: Accessor<boolean>
  // Fixed slot list from creation; each slot fills in place as it hydrates
  // (ADR 0024).
  classicSlots: CatalogueGridSlot[]
  credit: (currency: keyof ShopBalances, amount: number) => void
  // undefined while currency hydration runs in the background (ADR 0023).
  currencyCatalogue: CurrencyCatalogueData | undefined
  currencySkeletonCounts: CurrencySkeletonCounts
  // The one entry-time snapshot; undefined while pending or after failure.
  dailyPickUnownedCount: number | undefined
  // Distinguishes an empty draw from an assortment whose cards were omitted.
  dailyPickResponseSize: number
  // Fixed daily-pick slot list in the collection's display order; slots fill
  // in place exactly like the Classic grid's.
  dailyPickSlots: CatalogueGridSlot[]
  debit: (currency: keyof ShopBalances, amount: number) => void
  // Fixed hero slot list in the main-product collection's display order.
  heroSlots: HeroSlot[]
  // The launch product parameter in force — tree_id or landskin_id with its
  // raw value (GLOSSARY.md "Shop launch deep link"); undefined on a plain open.
  launchProduct: LaunchProduct | undefined
  // The launch deep link's detail dialog reports itself presented — assets
  // settled, images decoded, enter animation finished — the reveal surface's
  // last member (GLOSSARY.md "Reveal surface"). Before the Shop reveal the
  // real Shop is inert, so only the launch deep link can open a detail and
  // every presentation reported before it is the deep link's.
  onDetailPresented: () => void
  // Called whenever the Classic tab is showing: the first call starts the
  // Classic reveal (ADR 0049), later ones no-op. Absent on a Classic landing.
  onClassicShowing?: () => void
  // Reports a Classic slot that came within reach of the screen, for
  // in-view hydration (GLOSSARY.md; ADR 0051).
  onClassicSlotWithinReach?: (slot: CatalogueGridSlot) => void
  onPlusPaywallSuccess: () => Promise<void>
  // The Daily Picks refresh button's tap, carrying the tier it quoted and
  // whether the pool on screen is exhausted (GLOSSARY.md "Exhausted pool").
  onRefresh: DailyPicksRefreshTap
  plusRowHidden: boolean
  // undefined while trial eligibility resolves in the background (ADR 0023).
  plusState: PassivePlusState | undefined
  // The Daily Picks refresh button's state (GLOSSARY.md "Daily Picks refresh").
  refresh: Accessor<DailyPicksRefreshState>
  // The face the Daily Picks cards show (GLOSSARY.md "Refresh reveal"). Like
  // the rest of the refresh, it is owned above the tabs: a card mounting on
  // a return to Featured arrives on the reveal's current face rather than
  // replaying it.
  refreshBreathing: Accessor<boolean>
  refreshFaceDown: Accessor<boolean>
  // The refresh purchase's HTTP 422 (GLOSSARY.md "Insufficient purchase
  // balance"): the flow lives above the tabs, but the top-up navigation it
  // asks for is the shell's, so it arrives as a verdict to react to. A
  // fresh object per shortage, so two shortages in a row both navigate.
  refreshShortage: Accessor<RefreshShortage | undefined>
  // True inside the last minute before the rollover (GLOSSARY.md "Last-minute
  // refresh lock"), judged by the flow off the Shop server clock.
  refreshLocked: () => boolean
  // True while a Daily Picks refresh is in flight. The flow lives above the
  // tabs, so a tab switch mid-refresh neither cancels nor restarts it
  // (ADR 0032); returning to Featured shows wherever it has got to.
  refreshPending: () => boolean
  // Whether the Shop reveal has happened (GLOSSARY.md "Shop reveal"):
  // nothing announces itself behind the boot skeleton, and the shell stays
  // inert beneath it.
  revealed: Accessor<boolean>
  serverNow: Accessor<number>
  // The starter pack's hero slot (GLOSSARY.md "Starter pack"); undefined =
  // no starter pack.
  starterPack: StarterPackSlot | undefined
  // True while a post-purchase starter-pack refetch is in flight: the slot
  // is about to change, so the offer dialog waits for its answer.
  starterPackRefetching: Accessor<boolean>
  userId: number
}) {
  // The launch parameters are fixed for the page load, so the launch product
  // is read once, untracked, like the tab param.
  const launchProduct = untrack(() => props.launchProduct)
  // The landing tab: Classic for a product deep link, resolved or not
  // (GLOSSARY.md "Shop launch deep link").
  const [tab, setTab] = createSignal<ShopTab>(
    resolveLandingTab(tabParam, launchProduct),
  )
  // A still-unresolved launch deep link auto-opens a detail when the grid
  // stage arrives; an explicit tab choice by the user outranks that intent.
  // Tabs are inert until the Shop reveal, so that choice can only come after
  // a deadline reveal that left the target pending.
  const [deepLinkPending, setDeepLinkPending] = createSignal(
    launchProduct !== undefined,
  )
  // One page view per tab activation — initial, user switches, and
  // programmatic switches (top-up redirects) alike. Signal equality already
  // swallows re-selecting the active tab. A product deep link lands on
  // Classic whether or not its product resolves, so that is its initial
  // activation (GLOSSARY.md "Shop analytics names").
  createEffect(() => {
    logPageView(pageNameByTab[tab()])
  })
  createEffect(() => {
    if (tab() === 'classic') props.onClassicShowing?.()
  })
  // Each tab remembers the page position the user left it at, for this page
  // load only (GLOSSARY.md "Tab scroll memory"). Shop scrolls the document
  // itself, so that is one `window.scrollY` per tab. Never rendered, so a
  // plain object will do; it lives here because the tab panels unmount on
  // every switch.
  const tabScrollY: Partial<Record<ShopTab, number>> = {}
  // Every post-launch tab switch passes through here so the outgoing tab's
  // position is recorded. A user tap restores the destination's remembered
  // position (a first visit starts at the top). A programmatic arrival that
  // brings its own scroll target — the product deep link, the top-up
  // redirect — skips the restore and lets that target decide: the top-up
  // section scroll runs inside `setTab`, so a restore after it would
  // override it, while the deep link's card scroll follows in a microtask
  // and would only flash past a restore.
  const switchTab = (next: ShopTab, options: { restore: boolean }) => {
    const current = tab()
    if (next === current) return
    tabScrollY[current] = window.scrollY
    setTab(next)
    if (options.restore) {
      window.scrollTo({ top: tabScrollY[next] ?? 0, behavior: 'instant' })
    }
  }
  const selectTab = (next: ShopTab) => {
    setDeepLinkPending(false)
    switchTab(next, { restore: true })
  }
  const [sort, setSort] = createSignal<ClassicSort>('default')
  // The sort control needs products to sort. Pending slots may yet hydrate,
  // but once every Classic slot is omitted the collection is empty and hides
  // it (GLOSSARY.md "Empty Shop collection"), as an empty candidate list does.
  const classicHasProducts = createMemo(() =>
    hasVisibleSlots(props.classicSlots),
  )
  // The content filter's and sort control's row is a Classic-tab affair with
  // products to act on; the catalogue's top padding follows it.
  const classicControlsVisible = () =>
    tab() === 'classic' && classicHasProducts()
  // The Classic content filter (GLOSSARY.md): the chosen kind, Trees by
  // default, kept for the page load across tab switches. The kind on screen
  // yields to the other once the chosen one has nothing left to show; the
  // chips themselves appear only while both kinds do. One sort serves both.
  const [chosenContent, setChosenContent] = createSignal<ClassicContentFilter>(
    DEFAULT_CLASSIC_CONTENT_FILTER,
  )
  const classicContent = createMemo(() =>
    resolveClassicContentFilter(chosenContent(), props.classicSlots),
  )
  const offersContentFilter = createMemo(() =>
    offersClassicContentFilter(props.classicSlots),
  )
  // Switching the kind starts the tab at the top: the scroll memory is per
  // tab, not per filter (GLOSSARY.md "Tab scroll memory").
  const selectContent = (content: ClassicContentFilter) => {
    if (content === classicContent()) return
    setChosenContent(content)
    window.scrollTo({ top: 0, behavior: 'instant' })
  }
  const [productDetail, setProductDetail] = createSignal<ProductDetailState>({
    isOpen: false,
    fromDeepLink: false,
    fromDailyPick: false,
  })
  // Every appearance is one exposure — card taps and the launch deep link's
  // auto-open alike. The product's content picks the detail shape: the tree
  // detail, or the land-skin detail without the phase preview.
  const openProductDetail = (
    product: CatalogueProduct,
    options: { fromDeepLink?: boolean; fromDailyPick?: boolean } = {},
  ) => {
    logComponentShow(productDialogName(product))
    setProductDetail({
      isOpen: true,
      product,
      fromDeepLink: options.fromDeepLink ?? false,
      fromDailyPick: options.fromDailyPick ?? false,
    })
  }
  const closeProductDetail = () =>
    setProductDetail((current) => ({ ...current, isOpen: false }))
  // The starter-pack detail (GLOSSARY.md "Starter pack"): mounted from the
  // slot's hydrated pack rather than the card, so the card's expiry never
  // closes it. Every appearance is one exposure under the Store product id,
  // as for the tree and land-skin details.
  const [starterPackDetailOpen, setStarterPackDetailOpen] = createSignal(false)
  const openStarterPackDetail = (pack: StarterPack) => {
    logComponentShow(`shop_product_dialog_${pack.product.id}`)
    setStarterPackDetailOpen(true)
  }
  // A starter-pack purchase in flight, from its card's price pill or its
  // detail's price button (GLOSSARY.md "Starter pack"). No confirmation: the
  // flow submits as it mounts. The surface it started from stays beneath the
  // IAP processing overlay, so a retryable error or a cancellation unmounts
  // the flow and the user is back where they started; a commit closes the
  // detail it started from, if any, and the Reward reveal takes over.
  const [starterPackPurchase, setStarterPackPurchase] = createSignal<{
    from: 'card' | 'detail'
    pack: StarterPack
  }>()
  // A land skin's icon (GLOSSARY.md "Land-skin art") from whichever grid holds
  // it hydrated — the starter pack names its land skins by gid alone.
  const catalogueLandSkinIconUrl = (gid: number) =>
    [
      ...props.classicSlots,
      ...props.dailyPickSlots,
      ...props.heroSlots.map((hero) => hero.slot),
    ].find(
      (slot) =>
        slot.content === 'land_skin' && slot.gid === gid && slot.product,
    )?.product?.productImage.src
  const purchaseStarterPack = (pack: StarterPack, from: 'card' | 'detail') => {
    if (starterPackPurchase()) return
    // The detail's purchase click (GLOSSARY.md "Shop analytics names"): it
    // submits the IAP directly, so the click counts as it starts the flow.
    if (from === 'detail') {
      logButtonClick(`shop_product_dialog_${pack.product.id}`)
    }
    setStarterPackPurchase({ from, pack })
  }
  // The last-opened product while its content matches — each detail shape
  // mounts only for its own content, and the dismissed dialog keeps its
  // product so the closing animation plays with content.
  const treeDetailProduct = () => {
    const product = productDetail().product
    return product?.content === 'tree_type' ? product : undefined
  }
  const landSkinDetailProduct = () => {
    const product = productDetail().product
    return product?.content === 'land_skin' ? product : undefined
  }
  // Capture the purchase entry point, not membership of the current grid:
  // Classic purchases deliberately do not reduce this estimate (ADR 0034).
  let unlockFromDailyPick = false
  // The entry snapshot stays fixed, so neither tab switches nor rotations
  // reset its session-wide deduction.
  const [dailyPickPurchases, setDailyPickPurchases] = createSignal(0)
  const remainingDailyPicks = () => {
    const seed = props.dailyPickUnownedCount
    return seed === undefined
      ? undefined
      : Math.max(0, seed - dailyPickPurchases())
  }
  // Only an affordable tap reaches this; insufficient balance navigates to top-up.
  const purchaseFromDetail = (purchaseProduct: CatalogueProduct) => {
    // One unlock at a time: a free unlock submits as its flow mounts, so a
    // second tap before the Unlock button disables must not log again.
    if (unlockProduct()) return
    logButtonClick(productDialogName(purchaseProduct))
    unlockFromDailyPick = productDetail().fromDailyPick
    setUnlockProduct(purchaseProduct)
  }
  const [unlockProduct, setUnlockProduct] = createSignal<CatalogueProduct>()
  // A free unlock's in-flight command, presented by the open detail
  // (GLOSSARY.md "Free product").
  const [freeUnlockPending, setFreeUnlockPending] = createSignal(false)
  const [exchangeProduct, setExchangeProduct] = createSignal<CoinProduct>()
  const [timeCrystalProduct, setTimeCrystalProduct] =
    createSignal<TimeCrystalProduct>()
  // Time Crystal packs bought this session: each completed purchase spends
  // the pack's First-purchase bonus (GLOSSARY.md "First-purchase bonus
  // (首購翻倍)"), without a catalogue refetch.
  const [
    spentFirstPurchaseBonusProductIds,
    setSpentFirstPurchaseBonusProductIds,
  ] = createSignal<ReadonlySet<number>>(new Set())
  // Live on the Shop server clock, so a closing window updates every reader.
  const firstPurchaseBonusOf: FirstPurchaseBonusOf = (product) =>
    activeFirstPurchaseBonus(
      product,
      props.serverNow(),
      spentFirstPurchaseBonusProductIds(),
    )
  // Whether the Time Crystals section leads the currency catalogue
  // (GLOSSARY.md "First-purchase bonus (首購翻倍)"): decided once, as the
  // catalogue hydrates with an active bonus, and given up only when a
  // purchase spends the last one — a window closing unbought moves nothing.
  // The memo runs eagerly, so the decision is made as the catalogue
  // hydrates whichever tab is showing; the latch keeps it off the clock.
  let timeCrystalsLeadAtHydration: boolean | undefined
  const [lastFirstPurchaseBonusSpent, setLastFirstPurchaseBonusSpent] =
    createSignal(false)
  const timeCrystalsLead = createMemo(() => {
    const catalogue = props.currencyCatalogue
    if (!catalogue || lastFirstPurchaseBonusSpent()) return false
    timeCrystalsLeadAtHydration ??= untrack(() =>
      anyFirstPurchaseBonusActive(catalogue.gem, firstPurchaseBonusOf),
    )
    return timeCrystalsLeadAtHydration
  })
  const [currencyTarget, setCurrencyTarget] = createSignal<CurrencyKind>()
  const [currencyArtworkStatuses, setCurrencyArtworkStatuses] = createSignal(
    new Map<number, CurrencyArtworkStatus>(),
  )
  // Ownership at bootstrap is candidate data, known per slot before
  // hydration; session unlocks accumulate on top of it. Every grid feeds the
  // one set — a product owned anywhere is owned everywhere, under its
  // (content, gid) identity.
  const seededOwnedKeys = createMemo(
    () =>
      new Set(
        [
          ...props.classicSlots,
          ...props.dailyPickSlots,
          ...props.heroSlots.map((hero) => hero.slot),
        ].flatMap((slot) =>
          slot.owned ? [catalogueKey(slot.content, slot.gid)] : [],
        ),
      ),
  )
  const [ownedKeys, setOwnedKeys] = createSignal(new Set<CatalogueKey>())
  const isOwned = (content: CatalogueContent, gid: number) =>
    ownedKeys().has(catalogueKey(content, gid)) ||
    seededOwnedKeys().has(catalogueKey(content, gid))
  // Free products unlocked this session that have jumped to their owned
  // place in the Classic order (GLOSSARY.md "Classic collection order"). One
  // joins when its unlock celebration starts rather than at commit, so it
  // never moves behind the reveal or the apply prompt.
  const [ownedPlaceKeys, setOwnedPlaceKeys] = createSignal(
    new Set<CatalogueKey>(),
  )
  const isInOwnedPlace = (content: CatalogueContent, gid: number) =>
    ownedPlaceKeys().has(catalogueKey(content, gid))
  // Unlock celebrations in flight (GLOSSARY.md "Unlock celebration"). The
  // shell owns the one-cycle clock so a tab switch mid-cycle consumes the
  // celebration instead of replaying it on return.
  const [celebratingKeys, setCelebratingKeys] = createSignal(
    new Set<CatalogueKey>(),
  )
  // When an unlock celebration starts: a free product first jumps to its
  // owned place — on the Classic tab the page scrolls to it, reduced motion
  // included — then the card plays its one confetti cycle.
  const startUnlockCelebration = (product: CatalogueProduct) => {
    const key = catalogueKey(product.content, product.gid)
    if (product.price.kind === 'free') {
      setOwnedPlaceKeys((current) => new Set(current).add(key))
      if (untrack(tab) === 'classic') scrollToCard(product.content, product.gid)
    }
    if (prefersReducedMotion()) return
    setCelebratingKeys((current) => new Set(current).add(key))
    setTimeout(() => {
      setCelebratingKeys((current) => {
        const next = new Set(current)
        next.delete(key)
        return next
      })
    }, CELEBRATION_DURATION_MS)
  }
  const isCelebrating = (content: CatalogueContent, gid: number) =>
    celebratingKeys().has(catalogueKey(content, gid))
  // The land-skin apply prompt (GLOSSARY.md) is on screen between a land
  // skin's reveal and its celebration.
  const [applyPromptOpen, setApplyPromptOpen] = createSignal(false)
  // An unlock is still resolving on screen while its success reveal is up —
  // ownership already landed, the flow commits before the reveal closes —
  // while a land skin's apply prompt is up, or while its celebration cycle
  // runs. The Featured section holds its grid until then (GLOSSARY.md "Super
  // Collector state").
  const unlockResolving = () =>
    unlockProduct() !== undefined ||
    applyPromptOpen() ||
    celebratingKeys().size > 0
  // What the success reveal's acknowledgement does. The unlock's hold passes
  // reveal → (prompt →) celebration with no gap: every signal write
  // propagates at once, so each next holder is raised before the previous
  // one is released.
  const finishUnlock = (product: CatalogueProduct) => {
    if (product.content !== 'land_skin') {
      startUnlockCelebration(product)
      setUnlockProduct(undefined)
      return
    }
    setApplyPromptOpen(true)
    // Unmount before opening: the flow's cleanup closes the shared dialog,
    // which would take a prompt opened ahead of it down too.
    setUnlockProduct(undefined)
    // A land skin's celebration waits for its apply prompt to leave the
    // screen, however it was answered (GLOSSARY.md "Unlock celebration").
    void openLandSkinApplyPrompt().then(() => {
      startUnlockCelebration(product)
      setApplyPromptOpen(false)
    })
  }
  // A purchase's reveal chain is still on screen (GLOSSARY.md "Starter pack
  // offer dialog"): an unlock's flow, success reveal, land-skin apply prompt
  // or unlock celebration; a coin exchange's or Time Crystal purchase's flow
  // through its reveal; a starter-pack purchase's flow until its
  // multi-item reveal's last Claim. Every Shop purchase's chain joins this
  // one predicate.
  const purchaseChainOnScreen = () =>
    unlockResolving() ||
    exchangeProduct() !== undefined ||
    timeCrystalProduct() !== undefined ||
    starterPackPurchase() !== undefined
  // Any dialog other than the offer dialog: a product detail (the launch
  // deep link's included, and the one still about to open), the
  // starter-pack detail, or anything through the shared dialog — the Daily
  // Picks explainer, a Daily Picks refresh dialog, an error dialog.
  const otherDialogOpen = () =>
    deepLinkPending() ||
    productDetail().isOpen ||
    starterPackDetailOpen() ||
    dialog.showing()

  // The starter pack offer dialog (GLOSSARY.md): once per (user id, product
  // id), the first clear moment Shop holds a hydrated, unowned pack — after
  // the Shop reveal, with no purchase chain or other dialog on screen and no
  // refetch about to change the pack. Until then it waits, unremembered;
  // being shown is remembered the moment it opens, however it is dismissed.
  const offerMemory = createStarterPackOfferMemory()
  const offerHeld = () =>
    !props.revealed() ||
    props.starterPackRefetching() ||
    purchaseChainOnScreen() ||
    otherDialogOpen()
  const [offerOpen, setOfferOpen] = createSignal(false)
  // Whether the dialog has been presented this session: it stays mounted
  // after closing, for its exit.
  const [offerPresented, setOfferPresented] = createSignal(false)
  // The image the dialog shows, fixed at opening so a later refetch never
  // swaps it mid-presentation: undefined when the pack has none or it failed
  // to load.
  const [offerImageUrl, setOfferImageUrl] = createSignal<string>()
  // The offer image's preload, by URL: the dialog waits for it, then shows
  // with the image, or without it once the load has failed.
  const [offerImages, setOfferImages] = createSignal<
    Record<string, 'failed' | 'loaded' | 'loading'>
  >({})
  // The pack still owed its offer dialog: hydrated, unowned, and not yet
  // shown to this user on this device.
  const packOwedOffer = () => {
    const slot = props.starterPack
    const pack = slot?.status === 'hydrated' ? slot.pack : undefined
    if (!pack || pack.owned) return undefined
    return offerMemory.hasShown(props.userId, pack.product.id)
      ? undefined
      : pack
  }
  // The image starts loading as soon as a pack is owed its offer, alongside
  // the Shop reveal rather than after it.
  createEffect(() => {
    const url = packOwedOffer()?.offerImageUrl
    if (!url || untrack(offerImages)[url]) return
    const productId = packOwedOffer()!.product.id
    const settle = (status: 'failed' | 'loaded') =>
      setOfferImages((current) => ({ ...current, [url]: status }))
    setOfferImages((current) => ({ ...current, [url]: 'loading' }))
    preloadImage(url).then(
      () => settle('loaded'),
      () => {
        settle('failed')
        logNonFatal('shop_starter_pack_offer_image_failed', {
          product_id: String(productId),
          url,
        })
      },
    )
  })
  createEffect(() => {
    const pack = packOwedOffer()
    if (!pack || offerOpen() || offerHeld()) return
    const imageUrl = pack.offerImageUrl
    const imageStatus = imageUrl ? offerImages()[imageUrl] : 'loaded'
    if (imageStatus !== 'loaded' && imageStatus !== 'failed') return
    const productId = pack.product.id
    // The card is gone once the deadline passes; there is nothing to view.
    const deadline = pack.eligibleUntilMs
    if (deadline !== undefined && untrack(props.serverNow) >= deadline) return
    offerMemory.remember(props.userId, productId)
    setOfferPresented(true)
    setOfferImageUrl(imageStatus === 'loaded' ? imageUrl : undefined)
    setOfferOpen(true)
  })
  const closeOffer = () => setOfferOpen(false)
  // The starter pack shimmer (GLOSSARY.md) waits while the offer dialog is
  // still to come — an unowned pack this device has not been shown — or
  // open. Being shown is remembered as the dialog opens, so closing it is
  // what lets the shimmer start.
  const offerAwaited = () => offerOpen() || packOwedOffer() !== undefined
  // After View offer, the shimmer also waits for the card to come to rest
  // in view.
  const [viewOfferScrolling, setViewOfferScrolling] = createSignal(false)
  let cancelViewOfferRest: (() => void) | undefined
  onCleanup(() => cancelViewOfferRest?.())
  const starterPackShimmerHeld = () => offerAwaited() || viewOfferScrolling()
  // View offer: the Featured tab, scrolled so the whole starter-pack card is
  // in view — never the detail. The unowned card leads the hero row, which
  // leads the tab, so that is the top of the page and the start of the row;
  // the card's scrollIntoView would tuck its top under the fixed header.
  const viewOffer = () => {
    // Held before the dialog closes, so the shimmer never starts mid-scroll.
    setViewOfferScrolling(true)
    closeOffer()
    switchTab('featured', { restore: false })
    queueMicrotask(() => {
      const card = catalogueSection?.querySelector(
        '[data-testid="starter-pack-card"]',
      )
      if (!card) {
        setViewOfferScrolling(false)
        return
      }
      window.scrollTo({ top: 0, behavior: 'smooth' })
      card.closest('ul')?.scrollTo({ left: 0, behavior: 'smooth' })
      cancelViewOfferRest = whenAtRest(card, () => setViewOfferScrolling(false))
    })
  }

  // Leaving a catalogue tab consumes any in-flight celebration — returning
  // mid-cycle must not restart it.
  createEffect(
    on(
      tab,
      () => {
        setCelebratingKeys((current) =>
          current.size > 0 ? new Set<CatalogueKey>() : current,
        )
      },
      { defer: true },
    ),
  )
  const currencyArtworkStatus = (productId: number) =>
    currencyArtworkStatuses().get(productId) ?? 'loading'
  const setCurrencyArtworkStatus = (
    product: CurrencyProduct,
    status: CurrencyArtworkStatus,
  ) => {
    let reportFailure = false
    setCurrencyArtworkStatuses((current) => {
      const previous = current.get(product.productId)
      if (previous === status || previous === 'failed') return current
      const next = new Map(current)
      next.set(product.productId, status)
      reportFailure = status === 'failed'
      return next
    })
    if (reportFailure) {
      console.error('[shop] currency artwork failed', {
        product_id: product.productId,
        url: product.imageUrl,
      })
    }
  }
  const activeLabel = () => {
    if (tab() === 'featured') return m.store_tab_featured()
    return tab() === 'classic' ? m.store_tab_classic() : m.store_tab_coins()
  }

  let catalogueSection: HTMLElement | undefined
  // Centres a Classic card once the grid has rendered it. Identity is
  // (content, gid): the other kind's card sharing the number must not take
  // the scroll.
  const scrollToCard = (content: CatalogueContent, gid: number) => {
    queueMicrotask(() => {
      catalogueSection
        ?.querySelector(`[data-content="${content}"][data-gid="${gid}"]`)
        ?.scrollIntoView({ behavior: 'instant', block: 'center' })
    })
  }

  // Launch deep link: the product parameter is the more specific intent and
  // wins over the tab param. Its slot hydrates ahead of the rest (ADR 0024),
  // so this resolves — and opens the detail — as soon as the target
  // settles, usually before the reveal. One-shot so dismissing the dialog
  // and returning later never re-opens or re-scrolls.
  createEffect(() => {
    if (!launchProduct || !deepLinkPending()) return
    const resolution = resolveDeepLinkProduct(launchProduct, props.classicSlots)
    if (resolution.status === 'pending') return
    setDeepLinkPending(false)
    if (resolution.status === 'unresolved') return
    // The detail opens over the Classic tab on its own kind's filter
    // (GLOSSARY.md "Shop launch deep link") — the user may have switched to
    // the other kind while the target was still hydrating.
    const { product } = resolution
    setChosenContent(product.content)
    switchTab('classic', { restore: false })
    openProductDetail(product, { fromDeepLink: true })
    scrollToCard(product.content, product.gid)
  })
  const topUpForProduct = (currency: CatalogueCurrency) => {
    setUnlockProduct(undefined)
    closeProductDetail()
    setCurrencyTarget(currency)
    switchTab('currency', { restore: false })
  }
  // A refresh the server refused for balance lands in the same place an
  // unaffordable tier's tap does (ADR 0032). Deferred: the accessor starts
  // empty, and only a real verdict navigates.
  createEffect(
    on(
      () => props.refreshShortage(),
      (shortage) => {
        if (shortage) topUpForProduct(shortage.currency)
      },
      { defer: true },
    ),
  )

  return (
    // Inert beneath the boot skeleton until the Shop reveal (ADR 0048): its
    // tabs, pills and cards take no tap, focus or screen-reader visit while
    // the skeleton shows. The dialogs render through portals outside it, so
    // the deep-link detail still presents — loads, decodes, animates —
    // beneath the skeleton. It also takes the reveal's crossfade in, from
    // faded and blurred to none at all once revealed: a lingering filter
    // would make it the containing block of its fixed header.
    <main
      data-testid="shop-shell"
      inert={!props.revealed()}
      class={cn(
        'relative min-h-screen min-w-[320px] bg-page',
        REVEAL_TRANSITION_CLASS,
        !props.revealed() && REVEAL_HIDDEN_CLASS,
      )}
    >
      <ShopHeader>
        <div class={HEADER_ROW_CLASS}>
          <CloseShopButton />
          <BalancePills balances={props.balances()} />
        </div>

        <div role="tablist" aria-label="Shop collections" class={TAB_ROW_CLASS}>
          <button
            type="button"
            role="tab"
            aria-selected={tab() === 'featured'}
            aria-controls="shop-catalogue"
            onClick={() => selectTab('featured')}
            class={TAB_BUTTON_CLASS}
          >
            {m.store_tab_featured()}
            <Show when={tab() === 'featured'}>
              <span class="absolute inset-x-1 bottom-0 h-0.5 rounded-full bg-ink" />
            </Show>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab() === 'classic'}
            aria-controls="shop-catalogue"
            onClick={() => selectTab('classic')}
            class={TAB_BUTTON_CLASS}
          >
            {m.store_tab_classic()}
            <Show when={tab() === 'classic'}>
              <span class="absolute inset-x-1 bottom-0 h-0.5 rounded-full bg-ink" />
            </Show>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab() === 'currency'}
            aria-controls="shop-catalogue"
            onClick={() => selectTab('currency')}
            class={TAB_BUTTON_CLASS}
          >
            {m.store_tab_coins()}
            <Show when={tab() === 'currency'}>
              <span class="absolute inset-x-1 bottom-0 h-0.5 rounded-full bg-ink" />
            </Show>
          </button>
        </div>

        {/* The content filter's and sort control's shared row, flush under
            the tabs. Only on the Classic tab, and only while the collection
            has visible slots; the chips only while both kinds do, the row
            keeping its height either way. Outside the tablist: a non-tab
            control in there is invalid ARIA. */}
        <Show when={classicControlsVisible()}>
          <SkeletonCrossfade
            revealed={props.classicRevealed}
            skeleton={<ClassicControlsPlaceholder />}
            testId="classic-controls-skeleton"
          >
            <div
              data-testid="classic-controls"
              class={CLASSIC_CONTROLS_ROW_CLASS}
            >
              <Show when={offersContentFilter()}>
                <div
                  role="group"
                  aria-label={m.store_tab_classic()}
                  data-testid="classic-filter"
                  class="flex gap-2"
                >
                  <For each={CLASSIC_CONTENT_FILTERS}>
                    {(content) => (
                      <button
                        type="button"
                        aria-pressed={classicContent() === content}
                        aria-controls="shop-catalogue"
                        data-testid="classic-filter-chip"
                        data-content={content}
                        onClick={() => selectContent(content)}
                        class={cn(
                          'flex items-center text-sm font-bold transition-colors',
                          CLASSIC_CHIP_SIZE_CLASS,
                          classicContent() === content
                            ? 'bg-white text-ink'
                            : 'bg-ink/10 text-ink/30 backdrop-blur-[8px]',
                        )}
                      >
                        {contentFilterLabel[content]()}
                      </button>
                    )}
                  </For>
                </div>
              </Show>
              <button
                type="button"
                aria-label={`${m.store_tab_classic()} ↕`}
                aria-controls="shop-catalogue"
                data-testid="classic-sort"
                data-sort={sort()}
                onClick={() => setSort(nextSort[sort()])}
                class={cn(
                  'ml-auto flex items-center justify-center text-[#176361]',
                  CLASSIC_SORT_SIZE_CLASS,
                )}
              >
                <SortIcon sort={sort()} />
              </button>
            </div>
          </SkeletonCrossfade>
        </Show>
      </ShopHeader>

      <section
        ref={(element) => {
          catalogueSection = element
        }}
        id="shop-catalogue"
        role="tabpanel"
        aria-label={activeLabel()}
        class={cn(
          CATALOGUE_COLUMN_CLASS,
          classicControlsVisible()
            ? CATALOGUE_TOP_UNDER_CLASSIC_CONTROLS
            : CATALOGUE_TOP,
        )}
      >
        <span data-testid="active-catalogue" class="sr-only">
          {activeLabel()}
        </span>
        {tab() === 'featured' ? (
          <FeaturedCatalogue
            balances={props.balances}
            dailyPickUnownedCount={remainingDailyPicks()}
            dailyPickResponseSize={props.dailyPickResponseSize}
            dailyPickSlots={props.dailyPickSlots}
            heroSlots={props.heroSlots}
            isCelebrating={isCelebrating}
            isOwned={isOwned}
            onPurchaseStarterPack={(pack) => purchaseStarterPack(pack, 'card')}
            onRefresh={props.onRefresh}
            onSelectDailyPick={(product) =>
              openProductDetail(product, { fromDailyPick: true })
            }
            onSelect={openProductDetail}
            onSelectStarterPack={openStarterPackDetail}
            onTopUp={topUpForProduct}
            refresh={props.refresh}
            refreshBreathing={props.refreshBreathing}
            refreshFaceDown={props.refreshFaceDown}
            refreshLocked={props.refreshLocked}
            refreshPending={props.refreshPending}
            serverNow={props.serverNow}
            starterPack={props.starterPack}
            starterPackShimmerHeld={starterPackShimmerHeld}
            unlockResolving={unlockResolving}
          />
        ) : null}
        {tab() === 'classic' ? (
          <SkeletonCrossfade
            revealed={props.classicRevealed}
            skeleton={
              <CatalogueGridPlaceholder
                class="animate-pulse"
                count={FIRST_REVEAL_COUNT}
              />
            }
            testId="classic-catalogue-skeleton"
          >
            <ClassicCatalogue
              content={classicContent()}
              isCelebrating={isCelebrating}
              isInOwnedPlace={isInOwnedPlace}
              isOwned={isOwned}
              onSelect={openProductDetail}
              onSlotWithinReach={props.onClassicSlotWithinReach}
              serverNow={props.serverNow}
              slots={props.classicSlots}
              sort={sort()}
            />
          </SkeletonCrossfade>
        ) : null}
        {tab() === 'currency' && (
          <CurrencyCatalogue
            artworkStatus={currencyArtworkStatus}
            catalogue={props.currencyCatalogue}
            firstPurchaseBonusOf={firstPurchaseBonusOf}
            onArtworkError={(product) =>
              setCurrencyArtworkStatus(product, 'failed')
            }
            onArtworkLoad={(product) =>
              setCurrencyArtworkStatus(product, 'loaded')
            }
            onSelectCoinExchange={(product) => {
              setCurrencyTarget(undefined)
              setExchangeProduct(product)
            }}
            onPlusPaywallSuccess={props.onPlusPaywallSuccess}
            onSelectTimeCrystalPurchase={setTimeCrystalProduct}
            plusRowHidden={props.plusRowHidden}
            plusState={props.plusState}
            serverNow={props.serverNow}
            skeletonCounts={props.currencySkeletonCounts}
            target={currencyTarget()}
            timeCrystalsLead={timeCrystalsLead()}
          />
        )}
      </section>

      <Show when={treeDetailProduct()}>
        {(product) => (
          <TreeDetail
            balances={props.balances}
            fromDeepLink={productDetail().fromDeepLink}
            open={productDetail().isOpen}
            onClose={closeProductDetail}
            onPresented={props.onDetailPresented}
            onPurchase={purchaseFromDetail}
            onTopUp={topUpForProduct}
            product={product()}
            serverNow={props.serverNow}
            unlockPending={freeUnlockPending()}
          />
        )}
      </Show>

      <Show when={landSkinDetailProduct()}>
        {(product) => (
          <LandSkinDetail
            balances={props.balances}
            fromDeepLink={productDetail().fromDeepLink}
            open={productDetail().isOpen}
            onClose={closeProductDetail}
            onPresented={props.onDetailPresented}
            onPurchase={purchaseFromDetail}
            onTopUp={topUpForProduct}
            product={product()}
            serverNow={props.serverNow}
            unlockPending={freeUnlockPending()}
          />
        )}
      </Show>

      <Show when={props.starterPack?.pack}>
        {(pack) => (
          <StarterPackDetail
            balances={props.balances}
            onClose={() => setStarterPackDetailOpen(false)}
            onPurchase={(purchasePack) =>
              purchaseStarterPack(purchasePack, 'detail')
            }
            open={starterPackDetailOpen()}
            pack={pack()}
            purchasePending={starterPackPurchase() !== undefined}
          />
        )}
      </Show>

      <Show when={starterPackPurchase()}>
        {(purchase) => (
          <StarterPackPurchaseFlow
            balances={props.balances}
            credit={props.credit}
            landSkinIconUrl={catalogueLandSkinIconUrl}
            onCancel={() => setStarterPackPurchase(undefined)}
            onCommitted={(pack) => {
              // Its trees and land skins join session ownership, owned
              // everywhere in Shop — with no unlock celebration or apply
              // prompt (GLOSSARY.md "Reward reveal").
              setOwnedKeys((current) => {
                const next = new Set(current)
                for (const content of pack.contents) {
                  if (content.kind === 'catalogue') {
                    next.add(catalogueKey(content.content, content.gid))
                  }
                }
                return next
              })
              if (purchase().from === 'detail') setStarterPackDetailOpen(false)
            }}
            onComplete={() => setStarterPackPurchase(undefined)}
            pack={purchase().pack}
            userId={props.userId}
          />
        )}
      </Show>

      <Show when={offerPresented()}>
        <StarterPackOfferDialog
          imageUrl={offerImageUrl()}
          onClose={closeOffer}
          onViewOffer={viewOffer}
          open={offerOpen()}
        />
      </Show>

      <Show when={unlockProduct()}>
        {(product) => (
          <TreeUnlockFlow
            balances={props.balances}
            onCancel={() => setUnlockProduct(undefined)}
            onDone={() => finishUnlock(product())}
            onPendingChange={setFreeUnlockPending}
            onTopUp={topUpForProduct}
            onSuccess={(purchased, paidAmount) => {
              if (purchased.price.kind === 'paid') {
                props.debit(purchased.price.currency, paidAmount)
              }
              if (
                unlockFromDailyPick &&
                !ownedKeys().has(catalogueKey(purchased.content, purchased.gid))
              ) {
                setDailyPickPurchases((count) => count + 1)
              }
              setOwnedKeys((current) => {
                const next = new Set(current)
                next.add(catalogueKey(purchased.content, purchased.gid))
                return next
              })
              closeProductDetail()
              // The one per-purchase sync (GLOSSARY.md "Shop close sync"): the
              // apply prompt's deep link leaves Shop without the close sync,
              // and the reveal's dwell time lets this one land first.
              if (purchased.content === 'land_skin') {
                void native.data.fullSync()
              }
            }}
            product={product()}
            serverNow={props.serverNow}
            userId={props.userId}
          />
        )}
      </Show>

      <Show when={exchangeProduct()}>
        {(product) => (
          <CurrencyExchangeFlow
            artworkStatus={() => currencyArtworkStatus(product().productId)}
            balances={props.balances}
            credit={props.credit}
            debit={props.debit}
            onCancel={() => setExchangeProduct(undefined)}
            onComplete={() => setExchangeProduct(undefined)}
            onArtworkError={() => setCurrencyArtworkStatus(product(), 'failed')}
            onArtworkLoad={() => setCurrencyArtworkStatus(product(), 'loaded')}
            product={product()}
            serverNow={props.serverNow}
            userId={props.userId}
          />
        )}
      </Show>

      <Show when={timeCrystalProduct()}>
        {(product) => (
          <TimeCrystalPurchaseFlow
            artworkStatus={() => currencyArtworkStatus(product().productId)}
            balances={props.balances}
            credit={props.credit}
            firstPurchaseBonusOf={firstPurchaseBonusOf}
            onArtworkError={() => setCurrencyArtworkStatus(product(), 'failed')}
            onArtworkLoad={() => setCurrencyArtworkStatus(product(), 'loaded')}
            onCancel={() => setTimeCrystalProduct(undefined)}
            onCommitted={(carriedBonus) => {
              setSpentFirstPurchaseBonusProductIds(
                (spent) => new Set([...spent, product().productId]),
              )
              const catalogue = props.currencyCatalogue
              if (
                carriedBonus &&
                catalogue &&
                !anyFirstPurchaseBonusActive(
                  catalogue.gem,
                  firstPurchaseBonusOf,
                )
              ) {
                setLastFirstPurchaseBonusSpent(true)
              }
            }}
            onComplete={() => setTimeCrystalProduct(undefined)}
            product={product()}
            userId={props.userId}
          />
        )}
      </Show>
    </main>
  )
}
